import { useEffect, useMemo, useState } from 'react';
import { CircleCheck, CircleSlash, CircleHelp, TriangleAlert, Users } from 'lucide-react';
import {
  useGetRegisterQuery,
  useMarkAttendanceMutation,
  useClearAttendanceMutation,
  getApiError,
  type AttendanceState,
  type RegisterRow,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatTile } from '@/components/ui/StatTile';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input, Select } from '@/components/ui/Field';
import { Mark } from '@/components/ui/Badge';
import { todayWAT } from '@/lib/datetime';

type StateFilter = 'all' | AttendanceState;

const STATE_LABEL: Record<AttendanceState, string> = {
  present: 'Present',
  absent: 'Absent',
  unmarked: 'Not marked',
};

const STATE_TONE: Record<AttendanceState, 'success' | 'warning' | 'neutral'> = {
  present: 'success',
  absent: 'warning',
  unmarked: 'neutral',
};

/*
  ═════════════════════════════════════════════════════════════════════════════
  THE REGISTER

  What this screen is for
  ───────────────────────
  A teacher looks at their class, decides, and records the decision. Both
  decisions — present and absent — are recorded, and a child nobody has looked
  at is shown as NOT MARKED rather than quietly counted as present.

  Why that distinction is the whole page
  ──────────────────────────────────────
  The old screen had one decision ("mark absent") and treated its absence as
  present. So on a Saturday, a public holiday, or any day the register simply
  was not taken, it read "4 on register, 4 present, 0 absent" — and the gate,
  which refuses to release a child marked absent, had nothing to refuse,
  because nobody had been marked anything. A register that cannot tell "all
  present" apart from "nobody checked" is not a register.

  Why selection, and why bulk marking is the normal path
  ─────────────────────────────────────────────────────
  A register is taken a class at a time: "everyone here except these two."
  Forty single clicks is how a register stops getting taken by October. So the
  rows are selectable, the toolbar acts on the selection, and the fast path is
  "select all in this class → Present → untick the two who are missing".

  Why bulk marks ask first
  ────────────────────────
  One click that writes thirty-eight decisions, attributed to the person who
  clicked, is worth a sentence of confirmation naming the number and the class.
  A single row does not get a dialog — that would be a dialog for every child
  in a forty-child class, which teaches people to dismiss dialogs.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function AttendancePage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const role = useAuthStore((s) => s.user?.role);
  const isTeacher = role === 'teacher';

  const [date, setDate] = useState(todayWAT);
  const [classFilter, setClassFilter] = useState('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<null | { status: AttendanceState; ids: string[] }>(null);

  const {
    data: register,
    isLoading,
    isError,
    error: registerError,
  } = useGetRegisterQuery({ institutionId, date }, { skip: !institutionId });

  const [markAttendance, markMutation] = useMarkAttendanceMutation();
  const [clearAttendance, clearMutation] = useClearAttendanceMutation();

  const rows = register?.rows ?? [];

  /* A selection that survived a change of date or filter would act on children
     who are no longer on screen. */
  useEffect(() => setSelected(new Set()), [date, classFilter]);

  /* Class options come from the register itself rather than a groups request,
     so a teacher — who sees only their own classes — gets exactly those. */
  const classes = useMemo(
    () => [...new Set(rows.map((r) => r.studentClass).filter(Boolean))].sort(),
    [rows],
  );

  const scoped = useMemo(
    () => (classFilter ? rows.filter((r) => r.studentClass === classFilter) : rows),
    [rows, classFilter],
  );

  const counts = useMemo(
    () => ({
      present: scoped.filter((r) => r.status === 'present').length,
      absent: scoped.filter((r) => r.status === 'absent').length,
      unmarked: scoped.filter((r) => r.status === 'unmarked').length,
    }),
    [scoped],
  );

  const visible = useMemo(
    () => (stateFilter === 'all' ? scoped : scoped.filter((r) => r.status === stateFilter)),
    [scoped, stateFilter],
  );

  const selectedVisible = visible.filter((r) => selected.has(r.studentId));
  const allVisibleSelected = visible.length > 0 && selectedVisible.length === visible.length;

  const busy = markMutation.isLoading || clearMutation.isLoading;
  const mutationError = markMutation.error ?? clearMutation.error;
  const hasFilters = Boolean(classFilter) || stateFilter !== 'all';

  const toggleRow = (studentId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((r) => next.delete(r.studentId));
      else visible.forEach((r) => next.add(r.studentId));
      return next;
    });

  /* One row goes straight through; a selection asks first. */
  const request = (status: AttendanceState, ids: string[]) => {
    if (ids.length === 0) return;
    if (ids.length === 1) void apply(status, ids);
    else setPending({ status, ids });
  };

  const apply = async (status: AttendanceState, ids: string[]) => {
    const result =
      status === 'unmarked'
        ? await clearAttendance({ institutionId, date, studentIds: ids })
        : await markAttendance({ institutionId, date, studentIds: ids, status });
    setPending(null);
    /* Only clear the selection when the write landed, so a failed bulk mark can
       be retried without re-ticking forty rows. */
    if (!('error' in result)) setSelected(new Set());
  };

  const columns: Column<RegisterRow>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          className="h-3.5 w-3.5 cursor-pointer accent-[hsl(var(--primary))]"
          aria-label={allVisibleSelected ? 'Clear selection' : 'Select everyone shown'}
          checked={allVisibleSelected}
          onChange={toggleAllVisible}
          disabled={visible.length === 0}
        />
      ),
      render: (r) => (
        <input
          type="checkbox"
          className="h-3.5 w-3.5 cursor-pointer accent-[hsl(var(--primary))]"
          aria-label={`Select ${r.fullName}`}
          checked={selected.has(r.studentId)}
          onChange={() => toggleRow(r.studentId)}
        />
      ),
      className: 'w-[36px]',
    },
    {
      key: 'fullName',
      header: 'Student',
      render: (r) => <span className="font-semibold text-foreground">{r.fullName}</span>,
    },
    {
      key: 'class',
      header: 'Class',
      render: (r) => <span className="text-muted-foreground">{r.studentClass || '—'}</span>,
      className: 'w-[150px]',
    },
    {
      key: 'status',
      header: 'Today',
      render: (r) => <Mark tone={STATE_TONE[r.status]}>{STATE_LABEL[r.status]}</Mark>,
      className: 'w-[110px]',
    },
    {
      key: 'markedAt',
      header: 'Decided',
      render: (r) =>
        r.markedAt ? (
          <span className="text-muted-foreground">
            {new Date(r.markedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        ),
      className: 'w-[90px] max-sm:hidden',
    },
    {
      key: 'actions',
      header: '',
      align: 'num',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          <Button
            variant={r.status === 'present' ? 'primary' : 'secondary'}
            size="sm"
            disabled={busy}
            onClick={() => request('present', [r.studentId])}
          >
            Present
          </Button>
          <Button
            variant={r.status === 'absent' ? 'danger' : 'secondary'}
            size="sm"
            disabled={busy}
            onClick={() => request('absent', [r.studentId])}
          >
            Absent
          </Button>
        </div>
      ),
      className: 'w-[180px]',
    },
  ];

  const pendingLabel = pending ? STATE_LABEL[pending.status].toLowerCase() : '';

  return (
    <PageLayout
      title="Attendance"
      subtitle="Every child gets a decision — present or absent. The ones nobody has decided show as not marked."
      breadcrumbs={[{ label: 'Gate' }, { label: 'Attendance' }]}
      contained={false}
      actions={
        <Input
          type="date"
          aria-label="Register date"
          className="w-[150px]"
          value={date}
          max={todayWAT()}
          onChange={(e) => setDate(e.target.value)}
        />
      }
      toolbar={
        <>
          <Select
            aria-label="Filter by class"
            className="w-[190px]"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
          >
            <option value="">{isTeacher ? 'My classes' : 'All classes'}</option>
            {classes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setClassFilter('');
                setStateFilter('all');
              }}
            >
              Clear filters
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* The tiles are the filter: having read "7 not marked", the next thing
            anyone wants is those seven. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile
            label="On register"
            value={scoped.length}
            icon={<Users size={17} />}
            tone="neutral"
            active={stateFilter === 'all'}
            onClick={() => setStateFilter('all')}
            hint={classFilter || (isTeacher ? 'My classes' : 'All classes')}
          />
          <StatTile
            label="Present"
            value={counts.present}
            icon={<CircleCheck size={17} />}
            tone="success"
            active={stateFilter === 'present'}
            onClick={() => setStateFilter('present')}
          />
          <StatTile
            label="Absent"
            value={counts.absent}
            icon={<CircleSlash size={17} />}
            tone={counts.absent > 0 ? 'warning' : 'neutral'}
            active={stateFilter === 'absent'}
            onClick={() => setStateFilter('absent')}
          />
          <StatTile
            label="Not marked"
            value={counts.unmarked}
            icon={<CircleHelp size={17} />}
            /* Danger, not neutral: an unmarked child at three o'clock is an
               open question the gate cannot answer. */
            tone={counts.unmarked > 0 ? 'danger' : 'success'}
            active={stateFilter === 'unmarked'}
            onClick={() => setStateFilter('unmarked')}
            hint={counts.unmarked > 0 ? 'Nobody has decided' : 'Register taken'}
          />
        </div>

        {mutationError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]"
          >
            <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
            {getApiError(mutationError)}
          </div>
        )}

        {/* The selection bar replaces the table head's own breathing room only
            while something is selected, so the table does not shift about. */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/25 bg-primary/[0.06] px-3 py-2">
            <span className="text-[11.5px] font-semibold text-foreground">
              {selected.size} selected
            </span>
            <div className="ml-auto flex flex-wrap items-center gap-1.5">
              <Button variant="primary" size="sm" disabled={busy} onClick={() => request('present', [...selected])}>
                Mark present
              </Button>
              <Button variant="danger" size="sm" disabled={busy} onClick={() => request('absent', [...selected])}>
                Mark absent
              </Button>
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => request('unmarked', [...selected])}>
                Clear
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-clip rounded-lg border border-border bg-card">
          <DataTable
            columns={columns}
            rows={visible}
            rowKey={(r) => r.studentId}
            loading={isLoading}
            error={isError || undefined}
            errorMessage={isError ? getApiError(registerError) : undefined}
            isRowSelected={(r) => selected.has(r.studentId)}
            emptyTitle={hasFilters ? 'Nobody matches this filter' : 'No children on the register'}
            emptyDescription={
              hasFilters
                ? 'Clear the filters to see the whole register.'
                : isTeacher
                  ? 'No classes are assigned to you yet. Ask the office to add them.'
                  : 'Add students to the register before taking attendance.'
            }
            emptyAction={
              hasFilters ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setClassFilter('');
                    setStateFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
            footer={
              register && !isLoading ? (
                <span className="text-[11px] text-muted-foreground">
                  {register.complete
                    ? `Register complete for ${date} — every child has a decision.`
                    : `${counts.unmarked} of ${scoped.length} still need a decision for ${date}.`}
                </span>
              ) : undefined
            }
          />
        </div>
      </div>

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => pending && void apply(pending.status, pending.ids)}
        title={
          pending?.status === 'unmarked'
            ? `Clear ${pending.ids.length} decisions?`
            : `Mark ${pending?.ids.length ?? 0} children ${pendingLabel}?`
        }
        confirmLabel={pending?.status === 'unmarked' ? 'Clear decisions' : `Mark ${pendingLabel}`}
        destructive={pending?.status !== 'present'}
        loading={busy}
        body={
          pending?.status === 'unmarked' ? (
            <>
              This removes the decision for {pending.ids.length} children on {date}. They go back to
              <strong className="text-foreground"> not marked</strong>, and the gate will have no
              answer for them.
            </>
          ) : (
            <>
              You are recording <strong className="text-foreground">{pendingLabel}</strong> for{' '}
              {pending?.ids.length} children on {date}
              {classFilter ? ` in ${classFilter}` : ''}, in your name.
              {pending?.status === 'absent' && ' The gate will refuse to release them.'}
            </>
          )
        }
      />
    </PageLayout>
  );
}
